import { useEffect, useMemo, useState } from 'react';

import { useFirestore, useFirestorePersonaAbsences } from '../../firebase/useFirestore.js';
import { useTaskDTO } from '../firebase/TaskDto.jsx';
import { client } from '../Client.js';
import roleColorByPersona from '../../helpers/calendar/roleColorByPersona.js';
import { personaColorById } from '../../helpers/calendar/personaAgendaColors.js';
import useAgendaSchedule from './useAgendaSchedule.js';

/**
 * Alles wat een `CalendarView` nodig heeft om de agenda van deze organisatie te
 * tonen: de taken binnen het zichtbare bereik, de afwezigheid, en de kleuren.
 *
 * WAAROM EEN HOOK: er zijn twee schermen die dezelfde agenda tonen — de pagina
 * 'Agenda' en de weergave 'Lijst en agenda' bij Huishoudens. Dat recept
 * overschrijven is precies hoe de twee uit elkaar gaan lopen: de een krijgt een
 * nieuw filter, de ander niet, en niemand ziet het.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @typedef {object} AgendaData
 * @property {Array<object>} visibleDocs - De taken, zonder roosterregels van niet-actieve deelnemers zonder uitstroomdatum
 * @property {Array<object>} personas - Álle deelnemers, ook niet-actieve; de roosterweergave heeft ze nodig
 * @property {(range: {start: Date, end: Date}) => void} setDateRange - Geeft FullCalendar's bereik door
 * @property {() => void} refresh - Haalt opnieuw op na een wijziging buiten de listener om
 * @property {Array<object>} personaAbsences - Voor de afwezig-markering
 * @property {Record<string, object>} roleColorByPersonaId - Rolbolletje per deelnemer
 * @property {Record<string, object>} personaColors - Eigen agendakleur per deelnemer
 * @property {Record<string, Date>} scheduleEndByPersonaId - Laatste roosterdag per deelnemer met een uitstroomdatum
 *
 * @returns {AgendaData}
 */
const useAgendaData = () => {
    const taskDTO = useTaskDTO();

    const [docs, setDocs] = useState([]);
    const [dateRange, setDateRange] = useState({ start: new Date(), end: new Date() });
    const [refreshCounter, setRefreshCounter] = useState(0);

    // De HELE lijst, niet alleen de actieve: het rooster geeft ook een regel aan
    // iemand die niet meer actief is maar nog afspraken heeft staan — anders
    // vallen die afspraken zonder melding uit beeld. Eén listener, dus dit kost
    // niets extra's ten opzichte van een query op Status.
    const personas = useFirestore('Personas');
    // Wiens rooster in de agenda staat: de actieve deelnemers, plus de
    // uitgestroomden met een uitstroomdatum — die tot en met die dag. Afwezigheid, kleur en
    // rolbolletje gelden voor precies dezelfde groep, anders staat er een
    // roosterblok zonder afwezig-markering.
    // Wie vóór de getoonde periode uitstroomde valt weg: geen lege regel in
    // het rooster en geen afwezigheid-listener voor iemand die er niet meer is.
    // Ook de taken zelf: niet-actieve roosterregels en subtaken (werk, geen
    // moment) blijven buiten de agenda. Dezelfde hook als de andere agenda's.
    const { visibleDocs, schedulePersonas, schedulePersonaIds, scheduleEndByPersonaId } =
        useAgendaSchedule(personas, docs, dateRange.start);
    const userRoles = useFirestore('UserRoles');

    const personaAbsences = useFirestorePersonaAbsences(schedulePersonaIds, client);

    const roleColorByPersonaId = useMemo(
        () => roleColorByPersona(
            Array.isArray(schedulePersonas) ? schedulePersonas : [],
            Array.isArray(userRoles) ? userRoles : []
        ),
        [schedulePersonas, userRoles]
    );

    // Eigen agendakleur per persona (Personas.AgendaColor) — kleurt het blok
    // zelf; het rolbolletje blijft ernaast staan.
    const personaColors = useMemo(
        () => personaColorById(Array.isArray(schedulePersonas) ? schedulePersonas : []),
        [schedulePersonas]
    );

    useEffect(() => {
        const unsubscribe = taskDTO.queryBy({
            query: (q) => q,
            dateStart: dateRange.start,
            dateEnd: dateRange.end,
            onUpdate: setDocs,
        });

        return () => unsubscribe();
    }, [dateRange, refreshCounter]); // eslint-disable-line

    return {
        visibleDocs,
        personas: Array.isArray(personas) ? personas : [],
        setDateRange,
        refresh: () => setRefreshCounter((c) => c + 1),
        personaAbsences,
        roleColorByPersonaId,
        personaColors,
        scheduleEndByPersonaId,
    };
};

export default useAgendaData;
